import { useState, useEffect } from "react";
import {
  collection,
  doc,
  updateDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { storageConst } from "../../utils/constants";
import { db, storage } from "../../lib/firebase";
import MainButton from "../../components/Button";
import SpinnerIcon from "../../assets/svgs/spinner";
import SkeletonProfile from "../../components/skeltonProfile";

export default function ProfilePage() {
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    about: "",
    avatar: "",
    isbusiness: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = localStorage.getItem(storageConst.APPTOKEN);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) {
        setError("User not logged in.");
        setLoading(false);
        return;
      }

      try {
        const userQuery = query(
          collection(db, "users"),
          where("uid", "==", userId)
        );
        const querySnapshot = await getDocs(userQuery);

        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0].data();
          setProfileData({
            name: userDoc.name || "",
            email: userDoc.email || "",
            about: userDoc.about || "",
            avatar: userDoc.avatar || "",
            isbusiness: userDoc.isbusiness || false,
          });
        } else {
          setError("User data not found.");
        }
      } catch (err) {
        console.error("Error fetching profile data:", err);
        setError("Failed to fetch profile data.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;

    setProfileData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? target.checked : value,
    }));
  };

  const handleUpdateProfile = async () => {
    setSaving(true);
    setError(null);

    try {
      if (!userId) throw new Error("User not logged in.");

      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, {
        name: profileData.name,
        email: profileData.email,
        about: profileData.about,
        avatar: profileData.avatar,
      });

      alert("Profile updated successfully!");
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageRef = ref(storage, `avatars/${file.name}`);
      const snapshot = await uploadBytes(imageRef, file);
      const imageUrl = await getDownloadURL(snapshot.ref);

      if (imageUrl) {
        setProfileData((prev) => ({ ...prev, avatar: imageUrl }));
      }
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-bgCard shadow-lg rounded-xl p-6 space-y-8">
        {loading ? (
          <SkeletonProfile />
        ) : (
          <>
            <div className="flex justify-center">
              <div className="relative rounded-full w-28 h-28 border-4 border-primary shadow-lg mb-4 overflow-hidden">
                {profileData.avatar ? (
                  <img
                    src={profileData.avatar}
                    alt={profileData.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full bg-gray-300">
                    <span className="text-xl font-medium text-gray-700">
                      {profileData.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}

                {/* Camera Icon Overlay */}
                <label
                  htmlFor="file-input"
                  className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer"
                >
                  <div className="flex flex-col items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 10l4.553 4.553M15 14l-4.553 4.553M9 10H5.553l4.553-4.553m-4 4h4M12 15l4.553-4.553M15 15l-4.553 4.553"
                      />
                    </svg>
                    <span className="text-xs text-white mt-1">Edit</span>
                  </div>
                </label>

                {/* Hidden File Input */}
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-text"
                >
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  className={`w-full pl-9 pr-2 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full `}
                  value={profileData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-text"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  className={`w-full pl-9 pr-2 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full `}
                  value={profileData.email}
                  placeholder="Enter your email"
                  disabled
                />
              </div>

              <div>
                <label
                  htmlFor="about"
                  className="block text-sm font-medium text-text"
                >
                  About
                </label>
                <textarea
                  id="about"
                  name="about"
                  value={profileData.about}
                  onChange={handleInputChange}
                  placeholder="Write about yourself"
                  className={`w-full pl-9 pr-2 focus-visible:outline-none  bg-bgCard border rounded-xl pt-3 `}
                ></textarea>
              </div>

              <MainButton
                className="w-full bg-primary hover:bg-primary/80 text-white py-3 rounded-full"
                onClick={handleUpdateProfile}
                label={saving ? <SpinnerIcon /> : "Update Profile"}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
